import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Unlock } from 'lucide-react'
import apiClient from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import { formatDateHuman } from '@/lib/utils'
import { NumericKeypad } from './NumericKeypad'

interface ReopenDayDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  dayId: string
  businessDate: string
}

const PIN_LENGTH = 4

/**
 * Escape hatch when a closed day needs to be fixed. Unlocks via
 * manager_pin_hash on the backend — any active admin or manager
 * PIN is accepted (see dayops.actorFromManagerOrAdminPin). The
 * most-recent-closed-day constraint in dayops/service.go limits
 * what this can reach to the day still visible on the page.
 *
 * UX note: this used to be four separate small input boxes with
 * auto-advance and a shake animation on error. Cashiers/owners
 * mistype PINs constantly; the single masked input with a big
 * keypad is more recoverable and matches phone-style PIN entry.
 */
export function ReopenDayDialog({
  open,
  onOpenChange,
  dayId,
  businessDate,
}: ReopenDayDialogProps) {
  const qc = useQueryClient()
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setPin('')
      setError('')
    }
  }, [open])

  const mut = useMutation({
    mutationFn: () => apiClient.reopenBusinessDay(dayId, { pin }),
    onSuccess: () => {
      toastHelpers.success(`Day ${businessDate} unlocked`)
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      onOpenChange(false)
    },
    // Only `invalid_pin` means the PIN was wrong. Everything else — a day
    // still open elsewhere (409), a server fault (500), an expired session
    // (401), the network being down — must say what it actually is.
    //
    // This previously printed "Wrong PIN. Try again." for EVERY failure. A
    // 500 from a unique-constraint violation therefore sent the operator off
    // rotating manager PINs for hours against an error that had nothing to do
    // with PINs. See audit/DAY_REOPEN_PHANTOM_DAY_2026-07-29.md.
    onError: (err: unknown) => {
      const e = err as { code?: string; status?: number; isNetworkError?: boolean; message?: string }
      if (e?.code === 'invalid_pin') {
        setError('Wrong PIN. Try again.')
        setPin('')
        return
      }
      if (e?.isNetworkError) {
        setError("Couldn't reach the server. Check the connection and try again.")
        return
      }
      setError(e?.message?.trim() || 'Unlock failed. Try again.')
    },
  })

  const canSubmit = pin.length === PIN_LENGTH && !mut.isPending
  const masked = '•'.repeat(pin.length) + '·'.repeat(Math.max(0, PIN_LENGTH - pin.length))

  return (
    <Dialog open={open} onOpenChange={(o) => !mut.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Unlock className="h-6 w-6 text-amber-500" aria-hidden="true" />
            Unlock day
          </DialogTitle>
          <DialogDescription className="text-base">
            This unlocks{' '}
            <span className="font-semibold text-foreground">
              {formatDateHuman(businessDate)}
            </span>{' '}
            so you can change the numbers. Lock it again after you're done.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reopen-pin" className="text-base">
              Enter your 4-digit PIN
            </Label>
            <div
              className="flex h-16 items-center justify-center rounded-xl border-2 text-3xl font-bold tracking-[0.5em] tabular-nums select-none"
              aria-hidden="true"
            >
              {masked}
            </div>
            {/* Hidden but focusable input so a physical keyboard still works. */}
            <Input
              id="reopen-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={PIN_LENGTH}
              value={pin}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH)
                setPin(next)
                if (error) setError('')
              }}
              className="sr-only"
              aria-label="PIN"
            />
            {error && (
              <p className="text-sm font-medium text-rose-600 dark:text-rose-400">{error}</p>
            )}
          </div>

          <NumericKeypad
            value={pin}
            onChange={(next) => {
              setPin(next.slice(0, PIN_LENGTH))
              if (error) setError('')
            }}
            hideDecimal
            maxLength={PIN_LENGTH}
          />
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            size="lg"
            className="h-12 flex-1"
            onClick={() => onOpenChange(false)}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            className="h-12 flex-1 bg-amber-600 hover:bg-amber-700 text-white"
            onClick={() => mut.mutate()}
            disabled={!canSubmit}
          >
            {mut.isPending ? 'Unlocking…' : 'Unlock day'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
